import React, { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useNavigate } from 'react-router-dom';
import { getKbDataChartMulti } from '../lib/api/kb-api';
import './KbBuyerSentimentChart.css';

const DEFAULT_REGION_ID = 195;

const formatDateTick = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const yy = String(date.getFullYear()).slice(2);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `'${yy}.${mm}`;
};

const formatDateOnly = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${yyyy}-${mm}`;
};

const toValue = (raw) => {
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

function KbBuyerSentimentChart({ regionId = DEFAULT_REGION_ID }) {
  const navigate = useNavigate();
  const [seriesRows, setSeriesRows] = useState([]);
  const [displayRegionName, setDisplayRegionName] = useState('전국');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedRegionId = Number(regionId) || DEFAULT_REGION_ID;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await getKbDataChartMulti({
          regionId: selectedRegionId,
          indexTypes: ['BUYER_SE_SELLER', 'BUYER_SE_BUYER', 'BUYER_SE_INDEX'],
          lookbackYears: 2,
        });
        const rows = Array.isArray(result) ? result : [];
        setSeriesRows(rows);
        const firstNamed = rows.find((row) => row?.regionNameKo)?.regionNameKo;
        setDisplayRegionName(firstNamed || '전국');
      } catch (e) {
        setSeriesRows([]);
        setError(e?.message || '매수우위지수 차트 데이터를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [selectedRegionId]);

  const timeline = useMemo(() => {
    const dateMap = new Map();

    seriesRows.forEach((row) => {
      const baseDate = row?.baseDate;
      const indexType = String(row?.indexType || '');
      const value = toValue(row?.value);
      if (!baseDate || value == null) return;

      if (!dateMap.has(baseDate)) {
        dateMap.set(baseDate, {
          date: baseDate,
          seller: null,
          buyer: null,
          index: null,
        });
      }

      const current = dateMap.get(baseDate);
      if (indexType === 'BUYER_SE_SELLER') current.seller = value;
      if (indexType === 'BUYER_SE_BUYER') current.buyer = value;
      if (indexType === 'BUYER_SE_INDEX') current.index = value;
    });

    return Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [seriesRows]);

  const latestSummary = useMemo(() => {
    if (timeline.length === 0) {
      return {
        dateText: '-',
        seller: null,
        buyer: null,
        neutral: null,
        index: null,
      };
    }

    const latest = timeline[timeline.length - 1];
    const seller = latest?.seller;
    const buyer = latest?.buyer;
    const neutral = seller != null && buyer != null ? Math.max(0, 100 - seller - buyer) : null;
    return {
      dateText: formatDateOnly(latest?.date),
      seller,
      buyer,
      neutral,
      index: latest?.index,
    };
  }, [timeline]);

  const chartOption = useMemo(() => {
    const dates = timeline.map((item) => item.date);
    const sellerValues = timeline.map((item) => item.seller);
    const buyerValues = timeline.map((item) => item.buyer);
    const neutralValues = timeline.map((item) => {
      if (item?.seller == null || item?.buyer == null) return null;
      return Math.max(0, 100 - item.seller - item.buyer);
    });
    const indexValues = timeline.map((item) => item.index);

    return {
      animationDuration: 500,
      grid: {
        left: 40,
        right: 40,
        top: 28,
        bottom: 34,
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params = []) => {
          if (!Array.isArray(params) || params.length === 0) return '';
          const dateText = formatDateOnly(params[0]?.axisValue);
          const sellerParam = params.find((p) => p?.seriesName === '매도자많음');
          const buyerParam = params.find((p) => p?.seriesName === '매수자많음');
          const neutralParam = params.find((p) => p?.seriesName === '비슷함');
          const indexParam = params.find((p) => p?.seriesName === '매수우위지수');
          const sellerVal = sellerParam?.data == null ? null : Number(sellerParam.data);
          const buyerVal = buyerParam?.data == null ? null : Number(buyerParam.data);
          const dominance = sellerVal == null || buyerVal == null
            ? '-'
            : (buyerVal > sellerVal ? '매수 우위' : sellerVal > buyerVal ? '매도 우위' : '균형');

          return [
            `<div style="font-weight:700;margin-bottom:4px;">${dateText}</div>`,
            `<div>매도자많음: <strong>${sellerParam?.data == null ? '-' : Number(sellerParam.data).toFixed(1)}%</strong></div>`,
            `<div>비슷함: <strong>${neutralParam?.data == null ? '-' : Number(neutralParam.data).toFixed(1)}%</strong></div>`,
            `<div>매수자많음: <strong>${buyerParam?.data == null ? '-' : Number(buyerParam.data).toFixed(1)}%</strong></div>`,
            `<div>매수우위지수: <strong>${indexParam?.data == null ? '-' : Number(indexParam.data).toFixed(1)}</strong></div>`,
            `<div style="margin-top:4px;">우위: <strong>${dominance}</strong></div>`,
          ].join('');
        },
      },
      legend: {
        top: 1,
        right: 4,
        itemWidth: 10,
        itemHeight: 8,
        textStyle: {
          color: '#475569',
          fontSize: 11,
          fontWeight: 700,
        },
        data: ['매도자많음', '비슷함', '매수자많음', '매수우위지수'],
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: {
          color: '#52627d',
          fontSize: 11,
          formatter: formatDateTick,
          hideOverlap: true,
        },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#dce4ee' } },
      },
      yAxis: [
        {
          type: 'value',
          name: '',
          min: 0,
          max: 200,
          axisLabel: {
            color: '#334155',
            fontSize: 11,
            formatter: (v) => `${v}`,
          },
          splitLine: {
            lineStyle: { color: '#e8edf4' },
          },
          nameTextStyle: {
            color: '#334155',
            fontWeight: 700,
            fontSize: 11,
          },
        },
        {
          type: 'value',
          name: '',
          position: 'right',
          min: 0,
          max: 100,
          axisLabel: {
            color: '#334155',
            fontSize: 11,
          },
          splitLine: { show: false },
          nameTextStyle: {
            color: '#334155',
            fontWeight: 700,
            fontSize: 11,
          },
        },
      ],
      series: [
        {
          name: '매수자많음',
          type: 'line',
          color: '#f97316',
          yAxisIndex: 1,
          stack: 'ratio',
          symbol: 'none',
          smooth: 0.2,
          lineStyle: { width: 0, color: '#f97316' },
          areaStyle: { color: '#f97316', opacity: 0.78 },
          data: buyerValues,
        },
        {
          name: '비슷함',
          type: 'line',
          color: '#d1d5db',
          yAxisIndex: 1,
          stack: 'ratio',
          symbol: 'none',
          smooth: 0.2,
          lineStyle: { width: 0, color: '#d1d5db' },
          areaStyle: {
            color: '#d1d5db',
            opacity: 0.74,
            decal: {
              symbol: 'rect',
              dashArrayX: [1, 0],
              dashArrayY: [2, 2],
              rotation: 0,
            },
          },
          data: neutralValues,
        },
        {
          name: '매도자많음',
          type: 'line',
          color: '#2563eb',
          yAxisIndex: 1,
          stack: 'ratio',
          symbol: 'none',
          smooth: 0.2,
          lineStyle: { width: 0, color: '#2563eb' },
          areaStyle: { color: '#2563eb', opacity: 0.72 },
          data: sellerValues,
        },
        {
          name: '매수우위지수',
          type: 'line',
          color: '#e11d48',
          yAxisIndex: 0,
          smooth: true,
          symbol: 'none',
          lineStyle: {
            width: 2.8,
            color: '#e11d48',
          },
          data: indexValues,
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: {
              color: '#8c99ab',
              width: 1,
              type: 'solid',
              opacity: 0.8,
            },
            label: { show: false },
            data: [{ yAxis: 100 }],
          },
        },
      ],
    };
  }, [timeline]);

  const sellerRatio = latestSummary.seller == null ? 0 : latestSummary.seller;
  const neutralRatio = latestSummary.neutral == null ? 0 : latestSummary.neutral;
  const buyerRatio = latestSummary.buyer == null ? 0 : latestSummary.buyer;

  return (
    <section className="kb-buyer-chart-card">
      <div className="kb-buyer-chart-header">
        <div className="kb-buyer-chart-title-wrap">
          <span className="kb-buyer-chart-title-icon" aria-hidden="true"><i className="fas fa-balance-scale" /></span>
          <h3>{displayRegionName} 매수 우위 지수</h3>
        </div>
      </div>

      <div className="kb-buyer-summary-card">
        <p>
          <strong>{latestSummary.dateText}</strong> 기준
          {' '}
          <span className="seller">매도자많음 {latestSummary.seller == null ? '-' : `${latestSummary.seller.toFixed(1)}%`}</span>,
          {' '}
          <span className="buyer">매수자많음 {latestSummary.buyer == null ? '-' : `${latestSummary.buyer.toFixed(1)}%`}</span>,
          {' '}
          매수우위지수 <strong>{latestSummary.index == null ? '-' : latestSummary.index.toFixed(1)}</strong>
        </p>
        <div className="kb-buyer-ratio-bar">
          <span className="seller" style={{ width: `${sellerRatio}%` }} />
          <span className="neutral" style={{ width: `${neutralRatio}%` }} />
          <span className="buyer" style={{ width: `${buyerRatio}%` }} />
        </div>
      </div>

      {error && <p className="kb-buyer-chart-error">{error}</p>}
      {!error && loading && <div className="kb-buyer-chart-state">차트 데이터를 불러오는 중...</div>}
      {!error && !loading && timeline.length === 0 && <div className="kb-buyer-chart-state">표시할 데이터가 없습니다.</div>}

      {!error && !loading && timeline.length > 0 && (
        <ReactECharts option={chartOption} style={{ width: '100%', height: '340px' }} opts={{ renderer: 'canvas' }} notMerge lazyUpdate />
      )}

      <div className="kb-buyer-chart-bottom-meta">
        <div className="kb-buyer-chart-bottom-left">
          <span className="kb-buyer-chart-unit-text">단위: 지수, %</span>
          <span className="kb-buyer-chart-source">제공 : KB부동산</span>
        </div>
        <button
          type="button"
          className="kb-buyer-bottom-link"
          onClick={() => navigate(`/kb-data?viewMode=table&indexType=BUYER_SELLER&regionIds=${selectedRegionId}&autoSearch=1`)}
        >
          자세히 보기 &gt;
        </button>
      </div>
    </section>
  );
}

export default KbBuyerSentimentChart;
